// External libraries
import * as Yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useState } from 'react';
import classes from 'classnames';
import useApplication from 'hooks/useApplication';
import useClient from 'hooks/useClient';
import useMandatoryFlowRedirection from 'hooks/utilities/useMandatoryFlowRedirection';

// Internal app dependencies
import { App } from 'App';

// Components
import {
  ArrowFullRightIcon,
  CheckLineIcon,
  CheckmarkRoundedIcon,
  GoogleIcon,
  Logo,
  QuestionIcon,
  QuestionRoundedIcon,
  Stars5Icon,
  ThumbsUpIcon,
  UserSupportIcon,
  WarningGreyRoundedIcon,
} from 'components/icons';
import {
  Button,
  Card,
  CardGroup,
  CheckboxActor,
  Link,
  Modal,
  Text,
  Title,
} from 'components';
import { Table } from 'components/molecules/Table/Table';
import LegalTermsModal from 'components/atoms/LegalTermsModal/LegalTermsModal';
import QuestionsAccordion from 'components/molecules/QuestionsAccordion/QuestionsAccordion';

// Hooks
import { useApplicationSummary } from 'hooks/useApplicationSummary';
import { useProduct } from 'hooks/useProduct';
import useIovation from 'hooks/useIovation';

// Utils and formatters
import { createTransLinksComponents } from 'utils/transLinksComponents';
import { formatApplicationSummary } from 'formatters/formatApplicationSummary';
import { PHONE_AFFILIATE, PHONE_LINK_AFFILIATE } from 'utils/constants';

// Styles and localizations
import es from 'localizations/es';
import styles from './FirstLoan.module.scss';

// Constants
const EXTERNAL_FAQ_URL = 'https://www.vivus.es/faq';

// Type definitions
type QuestionAnswer = { question: string; answer: string };

/**
 * AffiliateFirstLoan Component
 *
 * This component handles the first loan application process for affiliate users.
 * It displays loan details, collects user consents, and manages the application flow.
 *
 * Key features:
 * - Displays loan summary with amount, interest, and repayment details
 * - Handles user consent checkboxes (company authorization, data processing, terms)
 * - Manages form validation and submission
 * - Provides FAQ section with product-specific questions
 * - Handles navigation based on application status and requirements
 */
export default function AffiliateFirstLoan() {
  const { navigateToNextMandatoryStep } = useMandatoryFlowRedirection();
  // Modal state management
  const [isModalOpened, setIsModalOpened] = useState(false); // Info modal state
  const [loading, setLoading] = useState(false); // Form submission loading state
  const [showModal, setShowModal] = useState(false); // Legal terms modal state

  // Navigation and API hooks
  const { client } = useClient();
  const { data: application } = useApplication();
  const { fetch: fetchPutClientSettings } = App.useApi(
    'client/settings',
    'PUT',
  );
  const { fetch: fetchPostAcceptAgreement } = App.useApi(
    'client/accept-latest-agreement-version',
    'POST',
  );

  // Business logic hooks
  const { amount, interest, repayableAmount, repaymentDate } =
    formatApplicationSummary(useApplicationSummary());

  // Initialize fraud detection
  useIovation();

  // Localization and product configuration
  const { t } = useTranslation('affiliates');
  const { valueRelatedToProduct } = useProduct();
  const valueRelatedToProductParsed = valueRelatedToProduct as
    | 'items_vivus'
    | 'items_vivusonline';

  // Modal handlers
  const handleModal = () => setShowModal(!showModal);

  // Form validation schema - only terms acceptance is required
  const validationSchema = Yup.object().shape({
    terms: Yup.bool().isTrue(t('validation:agreement_required')), // Terms must be accepted to proceed
  });

  // Processes the loan application + redirect client
  const onSubmit = async ({
    auth_company: acceptNews,
    auth_me: acceptDataSharing,
    terms,
  }: any) => {
    try {
      setLoading(true);

      // Only proceed if terms are accepted (validation should prevent this, but double-check)
      if (!terms) {
        return;
      }

      // Accept the latest legal agreement version
      await fetchPostAcceptAgreement({
        body: {},
      });

      // Save user consent preferences
      await fetchPutClientSettings({
        body: {
          acceptDataSharing,
          acceptNews,
        },
      });

      await navigateToNextMandatoryStep({
        client,
        application,
      });
    } catch (error) {
      // Error handling is intentionally silent to avoid exposing sensitive information
    } finally {
      setLoading(false);
    }
  };

  // Form configuration with Formik
  const formik = useFormik({
    initialValues: {
      auth_me: false, // Data processing authorization
      auth_company: false, // Company communication authorization
      terms: false, // Terms and conditions acceptance (required)
    },
    onSubmit, // Handle form submission
    validationSchema, // Apply validation rules
  });

  // Toggles all checkboxes based on current state:
  const onSelectAll = () => {
    const nextValue = !Object.values(formik.values).every(
      (valueAgreement: boolean) => valueAgreement, // Check if all checkboxes are currently selected
    );

    formik.setValues({
      auth_company: nextValue,
      auth_me: nextValue,
      terms: nextValue,
    });
  };

  // FAQ configuration based on product type
  const faqItemsKey = ['items_vivus', 'items_vivusonline'].includes(
    valueRelatedToProductParsed,
  )
    ? valueRelatedToProductParsed
    : 'items_vivus'; // Default fallback

  /**
   * FAQ items generation
   *
   * Maps through the localized FAQ items for the specific product type
   * and creates the question-answer pairs for the accordion component
   */
  const faqItems = es.affiliates.firstapplication.faq[faqItemsKey].map(
    (_, index) => {
      const items: QuestionAnswer[] = t(`firstapplication.faq.${faqItemsKey}`, {
        returnObjects: true,
      }) as unknown as QuestionAnswer[];

      return {
        question: items[index].question,
        answer: items[index].answer,
      };
    },
  );

  // Formatted loan values with currency symbols
  const values = {
    amount: `${amount} ${t('common:currencySymbol')}`,
    interest: `${interest} ${t('common:currencySymbol')}`,
    repayableAmount: `${repayableAmount} ${t('common:currencySymbol')}`,
    repaymentDate,
  };

  // Table row configuration for loan summary
  // Using consistent styling classes for all rows
  const commonRowClasses = classes(styles['row'], styles['text-primary']);
  const commonValueClasses = classes(
    styles['row-table-value'],
    styles['font-weight-semibold'],
  );

  const items = [
    {
      label: t('firstapplication.table.amount_request'),
      value: values.amount,
      className: commonRowClasses,
      classNameValue: commonValueClasses,
    },
    {
      label: t('firstapplication.table.interests'),
      value: values.interest,
      className: commonRowClasses,
      classNameValue: commonValueClasses,
    },
    {
      label: t('firstapplication.table.return_total'),
      value: values.repayableAmount,
      className: commonRowClasses,
      classNameValue: commonValueClasses,
    },
    {
      label: t('firstapplication.table.return_date'),
      value: values.repaymentDate,
      className: commonRowClasses,
      classNameValue: commonValueClasses,
    },
  ];

  // Placeholder for legal terms handler - currently unused
  const onClickLink = () =>
    window.open(EXTERNAL_FAQ_URL, '_blank', 'noopener,noreferrer');

  const ContinueBlock = (
    <div className={classes(styles['continue'], styles['text-primary'])}>
      <div className={styles['card-padding']}>
        <div
          className={classes(
            styles['continue-title'],
            styles['font-weight-semibold'],
            styles['font-large'],
          )}
        >
          {t('firstapplication.continue.title')}
        </div>
        <div
          className={classes(
            styles['continue-points'],
            styles['font-medium'],
            styles['font-weight-medium'],
          )}
        >
          <div>
            <ThumbsUpIcon data-testid="icon-ThumbsUpIcon-1" />
            <span>{t('firstapplication.continue.point1', { amount })}</span>
          </div>
          <div>
            <ThumbsUpIcon data-testid="icon-ThumbsUpIcon-2" />
            <span>{t('firstapplication.continue.point2')}</span>
          </div>
          <div>
            <ThumbsUpIcon data-testid="icon-ThumbsUpIcon-3" />
            <span>{t('firstapplication.continue.point3')}</span>
          </div>
        </div>
        <div className={styles['continue-icons']}>
          <GoogleIcon />
          <Stars5Icon />
          <span className={styles['font-weight-bold']}>
            {t('firstapplication.continue.excellent')}
          </span>
        </div>
        <div
          className={classes(
            styles['continue-footer'],
            styles['font-medium'],
            styles['font-weight-medium'],
          )}
        >
          <QuestionIcon data-testid="icon-QuestionIcon" />
          <Link
            href="#"
            onClick={() => setIsModalOpened(true)}
            dataTest="firstLoan-button-subtitle"
          >
            <span className={styles['font-medium']}>
              {t('firstapplication.button_sub_title')}
            </span>
          </Link>
        </div>
      </div>
    </div>
  );

  const QuestionFaq = (
    <span
      className={classes(
        styles['faq-title-right'],
        styles['text-primary'],
        styles['font-regular'],
      )}
      data-testid="firstLoan-faq-title-right"
    >
      <Trans
        i18nKey="affiliates:firstapplication.faq.header_right"
        components={createTransLinksComponents(onClickLink)}
      />
    </span>
  );

  return (
    <div className={styles['layout-loan']}>
      <div className={styles['header']}>
        <span
          dangerouslySetInnerHTML={{ __html: t('firstapplication.header') }}
        ></span>
        <ArrowFullRightIcon />
        <Logo />
      </div>
      <div className={styles['body-layout']}>
        <div>
          <Title
            className={styles['welcome-title']}
            icon={<CheckmarkRoundedIcon />}
            text={t('firstapplication.title_mobile', {
              amount,
            })}
            textClassName={styles['welcome-title-text']}
            iconClassName={styles['welcome-title-icon']}
          />
        </div>
        <CardGroup className={styles['card-group']} desktopAlign="row">
          <Card className={styles['loan-card']}>
            <div className={styles['card-padding']}>
              <div
                className={classes(
                  styles['table-title'],
                  styles['font-large'],
                  styles['font-weight-semibold'],
                )}
              >
                {t('firstapplication.table.title')}
              </div>
              <Table className={styles['table']} items={items} />
              <div
                className={classes(
                  styles['table-footer'],
                  styles['text-secondary'],
                  styles['font-small'],
                  styles['font-weight-medium'],
                )}
              >
                <WarningGreyRoundedIcon />
                <span>{t('firstapplication.table.footer')}</span>
              </div>
            </div>
            <div className={styles['desktop-only']}>{ContinueBlock}</div>
          </Card>
          <Card
            className={classes(styles['consents-card'], styles['text-primary'])}
          >
            <div
              className={classes(
                styles['consents-title'],
                styles['desktop-only'],
                styles['font-weight-semibold'],
                styles['font-large'],
              )}
            >
              {t('firstapplication.consents_title', {
                amount,
              })}
            </div>
            <form onSubmit={formik.handleSubmit} data-test="form">
              <Button
                leftIcon={<CheckLineIcon />}
                loading={loading}
                fill={true}
                className={classes(
                  styles['select-all-button'],
                  styles['font-small'],
                  styles['font-weight-semibold'],
                )}
                onClick={onSelectAll}
                outlined
                dataTest="firstLoan-select-checkbox"
              >
                {t('firstapplication.selectAll')}
              </Button>
              <div className={styles['checkboxes-wrapper']}>
                <CheckboxActor
                  labelClassName={classes(
                    styles['checkbox'],
                    styles['font-small'],
                  )}
                  classNameInput={styles['checkbox-input']}
                  formik={formik}
                  name="auth_company"
                  textHTML={t('firstapplication.authorization_company')}
                  dataTest="checkboxCompany"
                />
                <CheckboxActor
                  labelClassName={classes(
                    styles['checkbox'],
                    styles['font-small'],
                  )}
                  formik={formik}
                  name="auth_me"
                  textHTML={t('firstapplication.authorization_me')}
                  dataTest="checkboxAuthorization"
                />
                <CheckboxActor
                  labelClassName={classes(
                    styles['checkbox'],
                    styles['font-small'],
                  )}
                  formik={formik}
                  name="terms"
                  text={
                    <Trans
                      i18nKey="affiliates:firstapplication.terms_condition"
                      components={createTransLinksComponents(handleModal)}
                    />
                  }
                  dataTest="checkboxTerms"
                />
              </div>
              <Button
                loading={loading}
                type="submit"
                fill={true}
                className={styles['submit-button']}
                dataTest="firstLoan-submit-button"
              >
                {t('firstapplication.button')}
              </Button>
            </form>
          </Card>
        </CardGroup>
        <div className={styles['mobile-only']}>{ContinueBlock}</div>
        <div className={styles['faq-wrapper']}>
          <div
            className={classes(styles['faq-title'], styles['text-primary'])}
            data-testid="firstLoan-faq"
          >
            <span
              className={classes(
                styles['faq-title-left'],
                styles['font-large'],
                styles['font-weight-semibold'],
              )}
            >
              <QuestionRoundedIcon />
              <span data-testid="firstLoan-faq-title-left">
                {t('firstapplication.faq.header_left')}
              </span>
            </span>
            <div className={styles['desktop-only']}>{QuestionFaq}</div>
          </div>
          <div
            className={styles['faq-accordion']}
            data-test="firstLoan-accordion"
          >
            <QuestionsAccordion list={faqItems} />
          </div>
          <div className={styles['mobile-only']}>{QuestionFaq}</div>
        </div>
      </div>
      <div
        className={classes(styles['footer'], styles['mobile-padding-out-card'])}
      >
        <div
          className={classes(
            styles['footer-title'],
            styles['text-primary'],
            styles['font-large'],
            styles['font-weight-semibold'],
          )}
        >
          {t('firstapplication.footer')}
        </div>
        <div className={styles['footer-phone-wrapper']}>
          <UserSupportIcon />
          <a
            href={`tel:${PHONE_LINK_AFFILIATE}`}
            className={styles['text-primary']}
          >
            {PHONE_AFFILIATE}
          </a>
        </div>
        <Text
          level={3}
          text={t('firstapplication.footerBusiness')}
          kind="div"
        />
        <Text
          level={3}
          dangerouslySetInnerHTML={{
            __html: t('common:businessHours').replace(/,\s?/g, '<br />'),
          }}
        />
      </div>
      <Modal
        className={styles['modal-container']}
        isOpen={isModalOpened}
        onClose={() => setIsModalOpened(false)}
        isMobileFullWidth
        dataTest="firstLoan-modal"
      >
        <Title
          forceColumn={true}
          icon={<QuestionIcon data-testid="icon-QuestionIcon-title" />}
          text={t('firstapplication.modal.title')}
          dataTest="firstLoan-modal-title"
        />
        <Text
          dangerouslySetInnerHTML={{
            __html: t(
              `firstapplication.modal.${valueRelatedToProductParsed}.text`,
            ),
          }}
          className={styles['modal-text']}
          dataTest="firstLoan-modal-text"
        />
        <Button
          onClick={() => setIsModalOpened(false)}
          dataTest="firstLoan-modal-submitBtn"
        >
          {t('firstapplication.modal.button')}
        </Button>
      </Modal>
      {showModal && <LegalTermsModal onClose={handleModal} />}
    </div>
  );
}
